import React, {
  forwardRef,
  Ref,
  useCallback,
  useEffect,
  useImperativeHandle,
  useState,
} from 'react';
import { format, parseISO } from 'date-fns';
import { FaPencilAlt, FaTrashAlt } from 'react-icons/fa';
import { useToast } from '../../../hooks/toast';
import api from '../../../services/api';
import Button from '../../../components/Button';
import Loading from '../../../components/Loading';
import Paginacao from '../../../components/Paginacao';
import PerfilCadastro, { ICreateCupom } from '../CupomCadastro';

import Modal from '../../../components/Modal';

import { IFormBusca } from '..';
import getResponseErrors from '../../../utils/getResponseErrors';
import { formatPrice } from '../../../utils/funcoes';

export interface IPaginacao {
  termoBusca: IFormBusca;
  limite: number;
  pagina: number;
}

export interface ICupom extends ICreateCupom {
  id: number;
  valorFormatado: String;
  dataInicioFormatada: string;
  dataFimFormatada: string;
  tipoFormatado: string;
}

export interface RefObject {
  addCupom(dadosCupom: ICreateCupom): Promise<void>;
}
type Props = {
  dados?: IFormBusca;
  ref: Ref<RefObject>;
};

interface IFormataCupom {
  cupom: ICupom[];
}

function formataCupom({ cupom }: IFormataCupom): ICupom[] {
  const listagemCupomFormatada = cupom.map(cup => {
    return {
      ...cup,
      valorFormatado: cup.tipo === 1 ? `${cup.valor}%` : formatPrice(cup.valor),
      dataInicioFormatada: format(
        parseISO(cup.dataInicio.toString()),
        'dd/MM/yyyy HH:mm',
      ),
      dataFimFormatada: format(
        parseISO(cup.dataFim.toString()),
        'dd/MM/yyyy HH:mm',
      ),
      tipoFormatado: cup.tipo === 1 ? '%' : 'R$',
    };
  });

  return listagemCupomFormatada;
}

const ListaCupons: React.FC<Props> = forwardRef(
  (prop: Props, ref: Ref<RefObject>) => {
    const [showModalCadastro, setShowModalCadastro] = useState(false);
    const { addToast } = useToast();
    const [paginacao, setPaginacao] = useState<IPaginacao | undefined>(
      undefined,
    );
    const [loading, setLoading] = useState(false);
    const [totalRegistros, setTotalRegistros] = useState<number | undefined>(
      undefined,
    );
    const [listagemCupom, setListagemCupom] = useState<ICupom[]>([]);

    const [cupomSelecionado, setCupomSelecionado] = useState<
      ICupom | undefined
    >(undefined);

    useEffect(() => {
      setPaginacao({
        termoBusca: prop.dados || { codigo: '', parceiro: '' },
        pagina: 1,
        limite: 20,
      });
    }, [prop.dados]);

    useEffect(() => {
      async function buscaCupons(): Promise<void> {
        if (paginacao) {
          try {
            setLoading(true);
            const { codigo, parceiro } = paginacao.termoBusca;
            let query = '';
            if (codigo !== '') query = `&codigo=${codigo}`;
            if (parceiro !== '') query = `${query}&parceiro=${parceiro}`;

            const response = await api.get<[ICupom[], number]>(
              `/cupom?pagina=${paginacao.pagina}&limite=${paginacao.limite}${query}`,
            );

            const cuponsApi: ICupom[] = response.data[0];

            const cuponsFormatado = formataCupom({ cupom: cuponsApi });

            setTotalRegistros(
              response.data[1] === 0 ? undefined : response.data[1],
            );

            setListagemCupom(cuponsFormatado);
          } catch (error) {
            const mensagem = getResponseErrors(error);
            addToast({
              type: 'error',
              title: 'Não foi possível carregar cupons',
              description: mensagem,
            });
            setTotalRegistros(0);
            setListagemCupom([]);
          } finally {
            setLoading(false);
          }
        }
      }
      if (paginacao) buscaCupons();
    }, [paginacao, addToast]);

    const deleteCupom = useCallback(
      async (id: number) => {
        setLoading(true);
        try {
          await api.delete(`/cupom/${id}`);
          setListagemCupom(CupState => CupState?.filter(cup => cup.id !== id));
        } catch (error) {
          const mensagem = getResponseErrors(error);
          addToast({
            type: 'error',
            title: 'Não foi possível deletar cupom',
            description: mensagem,
          });
        } finally {
          setLoading(false);
        }
      },
      [addToast],
    );

    const addCupom = useCallback(
      async (dadosCupom: ICreateCupom) => {
        let response;
        setLoading(true);

        try {
          if (cupomSelecionado) {
            response = await api.put<ICupom>(
              `/cupom/${cupomSelecionado.id}`,
              dadosCupom,
            );
          } else {
            response = await api.post<ICupom>('/cupom', dadosCupom);
          }

          const {
            id,
            codigo,
            parceiro,
            tipo,
            valor,
            dataInicio,
            dataFim,
            maxUtilizacao,
          } = response.data;

          const retornoCupom = formataCupom({
            cupom: [
              {
                id,
                codigo,
                parceiro,
                tipo,
                valor,
                dataInicio,
                dataFim,
                maxUtilizacao,
                dataFimFormatada: '',
                dataInicioFormatada: '',
                valorFormatado: '',
                tipoFormatado: '',
              },
            ],
          });

          setListagemCupom(state => {
            return state
              ? [retornoCupom[0]].concat(
                  state.filter(cup => cup.id !== retornoCupom[0].id),
                )
              : [retornoCupom[0]];
          });

          setShowModalCadastro(false);
          setCupomSelecionado(undefined);
          addToast({ title: 'Cupom salvo', type: 'success' });
        } catch (error) {
          const mensagem = getResponseErrors(error);
          addToast({
            type: 'error',
            title: 'Não foi possível salvar cupom',
            description: mensagem,
          });
        } finally {
          setLoading(false);
        }
      },
      [addToast, cupomSelecionado],
    );

    useImperativeHandle(ref, () => ({ addCupom }));

    const handleProximaPagina = useCallback((page: number) => {
      setPaginacao(state => {
        return state && { ...state, pagina: page };
      });
    }, []);

    const handleDeleteCupom = useCallback(
      async (codigo: number) => {
        try {
          await deleteCupom(codigo);
          addToast({
            type: 'success',
            title: 'Cupom excluído',
          });
        } catch (error) {
          const mensagemErro = getResponseErrors(error);

          addToast({
            type: 'error',
            title: 'Não foi possível excluir o cupom',
            description: mensagemErro,
          });
        }
      },
      [addToast, deleteCupom],
    );

    const handleEditar = useCallback(cup => {
      setCupomSelecionado(cup);
      setShowModalCadastro(true);
    }, []);

    return (
      <>
        {showModalCadastro && (
          <Modal
            show={showModalCadastro}
            titulo="Cadastro de Cupom"
            handleFecharModal={() => setShowModalCadastro(false)}
            large
          >
            <PerfilCadastro cupom={cupomSelecionado} addCupom={addCupom} />
          </Modal>
        )}
        {loading && <Loading />}

        {listagemCupom && listagemCupom.length > 0 ? (
          <>
            <table
              className="table table-hover table-sm table-font-small"
              style={{ borderWidth: '0px' }}
            >
              <tbody>
                <tr className="bg-light" style={{ fontSize: 'Small' }}>
                  <th scope="col">Código</th>
                  <th scope="col">Parceiro</th>
                  <th scope="col">Tipo</th>
                  <th scope="col">Valor</th>
                  <th scope="col">Início</th>
                  <th scope="col">Fim</th>
                  <th scope="col">Nº Cupons</th>

                  <th scope="col">&nbsp;</th>
                  <th scope="col">&nbsp;</th>
                </tr>
                {listagemCupom.map(lista => (
                  <tr
                    key={`cupom-${lista.id}`}
                    style={{
                      fontSize: 'Small',
                    }}
                  >
                    <td style={{ width: '75px' }}>{lista.codigo}</td>
                    <td>{lista.parceiro}</td>
                    <td style={{ width: '35px' }}>{lista.tipoFormatado}</td>
                    <td style={{ width: '55px' }}>{lista.valorFormatado}</td>
                    <td style={{ width: '120px' }}>
                      {lista.dataInicioFormatada}
                    </td>
                    <td style={{ width: '120px' }}>{lista.dataFimFormatada}</td>
                    <td style={{ width: '75px' }}>{lista.maxUtilizacao}</td>
                    <td className="text-center" style={{ width: '40px' }}>
                      <Button
                        type="button"
                        variant="secondary-outline"
                        className="py-0"
                        title={`${lista.id}`}
                        onClick={() => {
                          handleDeleteCupom(lista.id);
                        }}
                      >
                        <FaTrashAlt />
                      </Button>
                    </td>
                    <td className="text-center" style={{ width: '40px' }}>
                      <Button
                        type="button"
                        variant="secondary-outline"
                        className="py-0"
                        title={`${lista.id}`}
                        onClick={() => {
                          handleEditar(lista);
                        }}
                      >
                        <FaPencilAlt />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <Paginacao
              limitePagina={paginacao?.limite || 15}
              paginasVizinhas={2}
              totalRegistros={totalRegistros}
              paginaInicio={paginacao?.pagina}
              handleProximaPagina={handleProximaPagina}
            />
          </>
        ) : (
          <div
            className="d-flex justify-content-center align-items-center"
            style={{ minHeight: '250px' }}
          >
            <h5>
              Nenhum registro encontrado. Pesquise Cupons pelo Código e/ou
              parceiro
            </h5>
          </div>
        )}
      </>
    );
  },
);

export default ListaCupons;
